import React, { useEffect, useRef, useState } from "react";
import { listingImage, listingFrames, slugHue, displayHost } from "../../../lib/marketplace";

/**
 * The capsule image at the top of every marketplace card.
 *
 * LIVE FRAMES ON HOVER
 * --------------------
 * When a listing has more than one asset, hovering cycles through them with a
 * cross-fade and a Steam-style tick strip along the bottom. This is the single detail
 * that makes a directory feel alive rather than static — and it costs nothing when
 * there is only one asset, because `listingFrames` returns [] and the cycling code
 * never mounts a timer.
 *
 * WHY THE FALLBACK IS THIS ELABORATE
 * ----------------------------------
 * Verified against the live API on 2026-08-10: all 14 prod listings have
 * screenshotUrls: [] and most have iconUrl: "". The capture pipeline exists but has
 * never been run against the fleet. A thumbnail-led grid with no thumbnails is
 * strictly WORSE than plain letter-squares: it trades small neat cards for a wall of
 * grey holes. So the no-image state is a *designed tile*, not a placeholder, and every
 * listing upgrades to real art silently as assets arrive — no code change.
 */
export default function ListingThumbnail({ listing, size = "md", hovered = false, className = "" }) {
  const frames = listingFrames(listing);
  const [frame, setFrame] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    // No timer at all unless there is genuinely something to cycle.
    if (!hovered || frames.length < 2) {
      setFrame(0);
      return undefined;
    }
    timer.current = setInterval(() => {
      setFrame((f) => (f + 1) % frames.length);
    }, 1100);
    return () => clearInterval(timer.current);
  }, [hovered, frames.length]);

  const src = listingImage(listing);
  const lg = size === "lg";

  if (src) {
    return (
      <div className={`relative aspect-[16/9] w-full overflow-hidden bg-black ${className}`}>
        {(frames.length ? frames : [src]).map((f, i) => (
          <img
            key={f}
            src={f}
            alt={i === 0 ? `${listing.name} preview` : ""}
            loading={i === 0 ? "lazy" : "eager"}
            aria-hidden={i !== 0}
            className={`absolute inset-0 h-full w-full object-cover object-top transition-all duration-500 ease-out ${
              i === frame ? "opacity-100" : "opacity-0"
            } ${hovered ? "scale-[1.04]" : "scale-100"}`}
          />
        ))}

        {/* Keeps the bottom edge readable where a screenshot happens to be pale. */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/75 via-black/5 to-transparent" />

        {/* Frame ticks — Valve's tell that there is more art behind the capsule. */}
        {frames.length > 1 && (
          <div
            className={`pointer-events-none absolute inset-x-0 bottom-0 flex gap-1 p-2 transition-opacity duration-200 ${
              hovered ? "opacity-100" : "opacity-0"
            }`}
          >
            {frames.map((f, i) => (
              <span
                key={f}
                className={`h-0.5 flex-1 transition-colors duration-300 ${
                  i === frame ? "bg-lime-400" : "bg-white/25"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  const hue = slugHue(listing.slug || listing.name);
  const monogram = (listing.name || "?").trim().charAt(0).toUpperCase();
  const host = displayHost(listing.appUrl);

  return (
    <div
      className={`relative aspect-[16/9] w-full overflow-hidden ${className}`}
      style={{ background: `linear-gradient(145deg, hsl(${hue} 42% 14%) 0%, #08080a 62%)` }}
      aria-hidden="true"
    >
      {/* Blueprint grid — reads as "app surface" rather than "missing asset". */}
      <div
        className="absolute inset-0 opacity-[0.13] transition-transform duration-700"
        style={{
          backgroundImage:
            "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
          backgroundSize: lg ? "44px 44px" : "26px 26px",
          transform: hovered ? "translate(-6px, -6px)" : "none",
        }}
      />
      <div
        className={`absolute -right-8 -top-10 rounded-full blur-3xl transition-opacity duration-500 ${
          lg ? "h-64 w-64" : "h-40 w-40"
        } ${hovered ? "opacity-100" : "opacity-70"}`}
        style={{ background: "rgba(163,230,53,0.18)" }}
      />

      {/* The hostname is deliberately NOT repeated here — the card body already prints
          it directly beneath this tile, and showing it twice was the single ugliest
          thing about the first live version. The spotlight (`lg`) has no such body,
          so it keeps the host. */}
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
        <div
          className={`flex items-center justify-center border font-bold transition-transform duration-300 ${
            lg ? "h-24 w-24 text-4xl" : "h-16 w-16 text-2xl"
          } ${hovered ? "scale-105" : "scale-100"}`}
          style={{
            borderColor: `hsl(${hue} 45% 38%)`,
            background: `hsl(${hue} 40% 20% / 0.8)`,
            color: `hsl(${hue} 60% 85%)`,
          }}
        >
          {monogram}
        </div>
        {lg && host && (
          <span className="max-w-[85%] truncate px-2 font-mono text-xs tracking-wide text-white/35">
            {host}
          </span>
        )}
      </div>
    </div>
  );
}
